import React, { useState, useRef } from 'react';
import { bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import {
  getDefaultTimeZoneOnBrowser,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
} from '../../util/dates';
import * as validators from '../../util/validators';
import {
  Form,
  FieldTextInput,
  FieldSelect,
  FieldSingleDatePicker,
  FieldTimeZoneSelect,
  FieldCheckbox,
  SecondaryButton,
  InlineTextButton,
  Icon,
} from '../../components';

import { looksLikeEmail, normalizeEmail } from '../../util/calendarInvites';
import css from './CreateEditEventForm.module.css';

const TODAY = new Date();

// Generate hours array for time select (00:00 - 23:30 in 30 min intervals)
const generateTimeOptions = intl => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const value = `${h}:${m}`;
      const label = intl.formatTime(new Date(2000, 0, 1, hour, minute), {
        hour: 'numeric',
        minute: '2-digit',
      });
      options.push({ value, label });
    }
  }
  return options;
};

// Format form's value for the date picker: convert timeOfDay to the local time
const formatFieldDateInput = timeZone => v =>
  v && v.date ? { date: timeOfDayFromTimeZoneToLocal(v.date, timeZone) } : { date: v };

// Parse the date picker's value: convert timeOfDay to the given time zone
const parseFieldDateInput = timeZone => v =>
  v && v.date ? { date: timeOfDayFromLocalToTimeZone(v.date, timeZone) } : v;

// Combine date and time into a single Date object
const combineDateAndTime = (date, timeString, timeZone) => {
  if (!date || !timeString) return null;

  const [hours, minutes] = timeString.split(':').map(Number);
  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);

  return combined;
};

// Extract time string from Date object
const extractTimeFromDate = date => {
  if (!date) return '09:00';
  const d = new Date(date);
  const hours = d
    .getHours()
    .toString()
    .padStart(2, '0');
  const minutes = d
    .getMinutes()
    .toString()
    .padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Date formatting used for placeholder texts
const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const initialsOf = label =>
  label
    ? label
        .trim()
        .split(/\s+/)
        .map(w => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';

// "Add participants" field. `options` are the selectable participants — team
// members first, then Audience Lists — each { id, type: 'team' | 'list', label }.
// Selected participants are stored on the form's `participants` value and shown
// as avatar chips.
const INVITE_NOTE_MAX = 140;
const SEARCH_DEBOUNCE_MS = 250;

// What is kept on the event for a picked participant. The email is only kept
// for an address typed by hand; audience contacts are resolved from the
// organizer's own records when the invitation is sent, so a later change to
// that record is respected.
const toParticipant = o => ({
  id: o.id,
  type: o.type,
  label: o.label,
  ...(o.type === 'email' ? { email: o.email } : {}),
});

/**
 * Who to invite. Team members come from the page (an org's team); everyone
 * else is found by searching the organizer's own audience on the server, or
 * added as a plain address. Nothing here can tell whether an address belongs
 * to an Attractr account.
 */
const ParticipantsField = ({ options, onSearch, intl }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  const latest = useRef(0);

  const runSearch = value => {
    const q = value.trim();
    if (timer.current) clearTimeout(timer.current);
    if (!onSearch || q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timer.current = setTimeout(async () => {
      const seq = latest.current + 1;
      latest.current = seq;
      try {
        const found = await onSearch(q);
        if (seq === latest.current) setResults(Array.isArray(found) ? found : []);
      } catch (e) {
        if (seq === latest.current) setResults([]);
      } finally {
        if (seq === latest.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleChange = e => {
    setQuery(e.target.value);
    runSearch(e.target.value);
  };

  return (
    <Field name="participants">
      {({ input }) => {
        const selected = input.value || [];
        const selectedIds = new Set(selected.map(p => p.id));
        const q = query.trim().toLowerCase();
        const team = options.filter(
          o =>
            o.type === 'team' && !selectedIds.has(o.id) && (!q || o.label.toLowerCase().includes(q))
        );
        const audience = results.filter(o => !selectedIds.has(o.id));
        const typedEmail = looksLikeEmail(q) ? normalizeEmail(q) : null;
        const typedOption =
          typedEmail &&
          !selectedIds.has(`email:${typedEmail}`) &&
          !audience.some(o => String(o.email || '').toLowerCase() === typedEmail)
            ? { id: `email:${typedEmail}`, type: 'email', label: typedEmail, email: typedEmail }
            : null;
        const hasDropdown =
          q && (team.length > 0 || audience.length > 0 || typedOption || searching);

        const add = o => {
          input.onChange([...selected, toParticipant(o)]);
          setQuery('');
          setResults([]);
          setSearching(false);
        };
        const remove = id => input.onChange(selected.filter(p => p.id !== id));
        const handleKeyDown = e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (typedOption) add(typedOption);
          }
        };

        const avatarFor = p => (
          <span className={p.type === 'email' ? css.participantAvatarEmail : css.participantAvatar}>
            {p.type === 'email' ? '@' : initialsOf(p.label)}
          </span>
        );

        const renderOption = o => (
          <button key={o.id} type="button" className={css.participantOption} onClick={() => add(o)}>
            {avatarFor(o)}
            <span className={css.participantOptionText}>
              <span>{o.label}</span>
              {o.type === 'audience' && o.email && o.email !== o.label ? (
                <span className={css.participantOptionSecondary}>{o.email}</span>
              ) : null}
            </span>
            {o.member ? (
              <span className={css.participantMemberBadge}>
                {intl.formatMessage({ id: 'CreateEditEventForm.participantsMember' })}
              </span>
            ) : null}
          </button>
        );

        const placeholderId = options.length
          ? 'CreateEditEventForm.participantsPlaceholder'
          : 'CreateEditEventForm.participantsPlaceholderNoTeam';

        return (
          <div className={css.participantsField}>
            <label className={css.participantsLabel}>
              {intl.formatMessage({ id: 'CreateEditEventForm.participantsLabel' })}
            </label>
            {selected.length > 0 && (
              <div className={css.participantChips}>
                {selected.map(p => (
                  <span key={p.id} className={css.participantChip}>
                    {avatarFor(p)}
                    <span className={css.participantChipLabel}>{p.label}</span>
                    <button
                      type="button"
                      className={css.participantChipRemove}
                      onClick={() => remove(p.id)}
                      aria-label={intl.formatMessage({
                        id: 'CreateEditEventForm.participantsRemove',
                      })}
                    >
                      <Icon name="close" size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text"
              className={css.participantsSearch}
              placeholder={intl.formatMessage({ id: placeholderId })}
              value={query}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              autoComplete="off"
            />
            {hasDropdown && (
              <div className={css.participantsDropdown}>
                {team.length > 0 && (
                  <div className={css.participantsGroup}>
                    {intl.formatMessage({ id: 'CreateEditEventForm.participantsTeam' })}
                  </div>
                )}
                {team.map(renderOption)}
                {audience.length > 0 && (
                  <div className={css.participantsGroup}>
                    {intl.formatMessage({ id: 'CreateEditEventForm.participantsAudience' })}
                  </div>
                )}
                {audience.map(renderOption)}
                {typedOption && (
                  <>
                    <div className={css.participantsGroup}>
                      {intl.formatMessage({ id: 'CreateEditEventForm.participantsByEmail' })}
                    </div>
                    <button
                      type="button"
                      className={css.participantOption}
                      onClick={() => add(typedOption)}
                    >
                      {avatarFor(typedOption)}
                      <span>
                        {intl.formatMessage(
                          { id: 'CreateEditEventForm.participantsAddEmail' },
                          { email: typedOption.email }
                        )}
                      </span>
                    </button>
                  </>
                )}
                {searching && audience.length === 0 && (
                  <div className={css.participantsSearching}>
                    {intl.formatMessage({ id: 'CreateEditEventForm.participantsSearching' })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }}
    </Field>
  );
};

const CreateEditEventFormComponent = props => {
  const [generatingMeeting, setGeneratingMeeting] = useState(false);
  const [generateMeetingError, setGenerateMeetingError] = useState(false);
  const [meetingLinkCopied, setMeetingLinkCopied] = useState(false);
  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          disabled,
          handleSubmit,
          intl,
          invalid,
          pristine,
          values,
          form,
          isEditMode,
          initialValues,
          submitInProgress,
          formError,
          onCancel,
          participantOptions = [],
          onSearchParticipants,
          onGenerateMeeting,
        } = fieldRenderProps;

        // Generate the event's video link via our Whereby integration — the
        // only way to set it (no manual paste). Populates the meetingUrl field.
        const handleGenerateMeeting = async () => {
          if (!onGenerateMeeting || generatingMeeting) return;
          setGeneratingMeeting(true);
          setGenerateMeetingError(false);
          try {
            // Expire the room a day after the event date (fallback: backend
            // default) so the link stays valid through the event.
            const eventDate = values?.endDate?.date || values?.startDate?.date || null;
            let endDate;
            if (eventDate) {
              const d = new Date(eventDate);
              d.setDate(d.getDate() + 1);
              endDate = d.toISOString();
            }
            const result = await onGenerateMeeting({ endDate, title: values?.title });
            // Back-compat: a plain string is treated as the participant url.
            const url = typeof result === 'string' ? result : result?.url;
            if (url) {
              form.change('meetingUrl', url);
              form.change('meetingHostUrl', (result && result.hostUrl) || '');
              form.change('meetingHostUserId', (result && result.hostUserId) || '');
            } else {
              setGenerateMeetingError(true);
            }
          } catch (e) {
            setGenerateMeetingError(true);
          } finally {
            setGeneratingMeeting(false);
          }
        };

        const timeZone = values.timeZone || getDefaultTimeZoneOnBrowser() || 'Etc/UTC';
        const timeOptions = generateTimeOptions(intl);

        // Labels and placeholders
        const titleLabel = intl.formatMessage({ id: 'CreateEditEventForm.titleLabel' });
        const titlePlaceholder = intl.formatMessage({ id: 'CreateEditEventForm.titlePlaceholder' });
        const titleRequiredMessage = intl.formatMessage({
          id: 'CreateEditEventForm.titleRequired',
        });

        const allDayLabel = intl.formatMessage({ id: 'CreateEditEventForm.allDayLabel' });

        const startDateLabel = intl.formatMessage({ id: 'CreateEditEventForm.startDateLabel' });
        const startTimeLabel = intl.formatMessage({ id: 'CreateEditEventForm.startTimeLabel' });
        const endDateLabel = intl.formatMessage({ id: 'CreateEditEventForm.endDateLabel' });
        const endTimeLabel = intl.formatMessage({ id: 'CreateEditEventForm.endTimeLabel' });

        const timeZoneLabel = intl.formatMessage({ id: 'CreateEditEventForm.timeZoneLabel' });

        const descriptionLabel = intl.formatMessage({ id: 'CreateEditEventForm.descriptionLabel' });
        const descriptionPlaceholder = intl.formatMessage({
          id: 'CreateEditEventForm.descriptionPlaceholder',
        });

        const meetingUrlLabel = intl.formatMessage({ id: 'CreateEditEventForm.meetingUrlLabel' });
        const autoGenerateLabel = intl.formatMessage({
          id: 'CreateEditEventForm.autoGenerateWhereby',
        });

        const inviteNoteLabel = intl.formatMessage({ id: 'CreateEditEventForm.inviteNoteLabel' });
        const inviteNotePlaceholder = intl.formatMessage({
          id: 'CreateEditEventForm.inviteNotePlaceholder',
        });
        const inviteNoteTooLong = intl.formatMessage(
          { id: 'CreateEditEventForm.inviteNoteTooLong' },
          { max: INVITE_NOTE_MAX }
        );
        const sendInvitesLabel = intl.formatMessage({ id: 'CreateEditEventForm.sendInvitesLabel' });
        const sendInvitesHint = intl.formatMessage({ id: 'CreateEditEventForm.sendInvitesHint' });
        const hasParticipants =
          Array.isArray(values.participants) && values.participants.length > 0;

        const locationLabel = intl.formatMessage({ id: 'CreateEditEventForm.locationLabel' });
        const locationPlaceholder = intl.formatMessage({
          id: 'CreateEditEventForm.locationPlaceholder',
        });

        const submitButtonText = submitInProgress
          ? intl.formatMessage({
              id: isEditMode
                ? 'CreateEditEventForm.submittingUpdate'
                : 'CreateEditEventForm.submittingCreate',
            })
          : intl.formatMessage({
              id: isEditMode
                ? 'CreateEditEventForm.submitUpdate'
                : 'CreateEditEventForm.submitCreate',
            });
        const cancelButtonText = intl.formatMessage({ id: 'CreateEditEventForm.cancel' });

        // Validation
        const titleValidation = validators.composeValidators(
          validators.required(titleRequiredMessage),
          validators.maxLength(intl.formatMessage({ id: 'CreateEditEventForm.titleTooLong' }), 100)
        );

        const dateRequiredMessage = intl.formatMessage({ id: 'CreateEditEventForm.dateRequired' });
        const dateValidation = validators.bookingDateRequired(dateRequiredMessage);

        // Auto-sync end date to start date when end date is empty OR
        // when the new start date is later than the existing end date
        // (otherwise the form would carry an invalid past-end state).
        const handleStartDateChange = value => {
          if (!value || !value.date) return;
          const hasEnd = values.endDate && values.endDate.date;
          if (!hasEnd) {
            form.change('endDate', value);
            return;
          }
          if (new Date(value.date) > new Date(values.endDate.date)) {
            form.change('endDate', value);
          }
        };

        // Handle start time change - auto-update end time if it's now invalid
        const handleStartTimeChange = newStartTime => {
          // If current end time is less than or equal to new start time, update end time
          if (values.endTime && values.endTime <= newStartTime) {
            // Find the next available time slot after the new start time
            const nextTimeOption = timeOptions.find(opt => opt.value > newStartTime);
            if (nextTimeOption) {
              form.change('endTime', nextTimeOption.value);
            }
          }
        };

        // Handle all-day toggle
        const isAllDay = values.allDay;

        const classes = classNames(rootClassName || css.root, className);
        const submitDisabled = invalid || disabled || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {/* Title */}
            <FieldTextInput
              id="title"
              name="title"
              type="text"
              className={css.field}
              label={titleLabel}
              placeholder={titlePlaceholder}
              validate={titleValidation}
              autoFocus
            />

            {/* Date/Time Fields */}
            {/* Start Date + End Date shown always so multi-day rehearsals
                and touring schedules work. When not all-day, Start Time
                + End Time appear in a second row. handleStartDateChange
                already syncs endDate to startDate when endDate is empty,
                so single-day events keep one-click behavior. */}
            <div className={css.dateTimeRow}>
              <div className={css.dateField}>
                <FieldSingleDatePicker
                  id="startDate"
                  name="startDate"
                  className={css.fieldDateInput}
                  label={startDateLabel}
                  placeholderText={intl.formatDate(TODAY, dateFormattingOptions)}
                  format={formatFieldDateInput(timeZone)}
                  parse={parseFieldDateInput(timeZone)}
                  validate={dateValidation}
                  onChange={handleStartDateChange}
                  useMobileMargins={false}
                  isOutsideRange={() => false}
                />
              </div>

              <div className={css.dateField}>
                <FieldSingleDatePicker
                  id="endDate"
                  name="endDate"
                  className={css.fieldDateInput}
                  label={endDateLabel}
                  placeholderText={intl.formatDate(TODAY, dateFormattingOptions)}
                  format={formatFieldDateInput(timeZone)}
                  parse={parseFieldDateInput(timeZone)}
                  validate={dateValidation}
                  useMobileMargins={false}
                  isOutsideRange={() => false}
                />
              </div>
            </div>

            {!isAllDay && (
              <div className={css.dateTimeRow}>
                <div className={css.timeField}>
                  <FieldSelect
                    id="startTime"
                    name="startTime"
                    className={css.fieldSelect}
                    label={startTimeLabel}
                    onChange={handleStartTimeChange}
                  >
                    {timeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </FieldSelect>
                </div>
                <div className={css.timeField}>
                  <FieldSelect
                    id="endTime"
                    name="endTime"
                    className={css.fieldSelect}
                    label={endTimeLabel}
                  >
                    {timeOptions
                      .filter(opt => {
                        // End-time must beat start-time only when the
                        // event ends on the same day as it starts. Across
                        // multiple days any time is valid.
                        const sameDay =
                          values.startDate?.date &&
                          values.endDate?.date &&
                          new Date(values.startDate.date).toDateString() ===
                            new Date(values.endDate.date).toDateString();
                        return sameDay ? opt.value > values.startTime : true;
                      })
                      .map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                  </FieldSelect>
                </div>
              </div>
            )}

            {/* All-day checkbox */}
            <div className={css.checkboxField}>
              <FieldCheckbox id="allDay" name="allDay" label={allDayLabel} />
            </div>

            {/* Timezone */}
            <FieldTimeZoneSelect
              id="timeZone"
              name="timeZone"
              className={css.field}
              label={timeZoneLabel}
            />

            {/* Description */}
            <FieldTextInput
              id="description"
              name="description"
              type="textarea"
              className={css.field}
              label={descriptionLabel}
              placeholder={descriptionPlaceholder}
              validate={validators.maxLength(
                intl.formatMessage({ id: 'CreateEditEventForm.descriptionTooLong' }),
                2000
              )}
            />

            {/* Video link — generated via our Whereby integration only (no
                manual paste). */}
            <div className={css.videoLinkBlock}>
              <label>{meetingUrlLabel}</label>
              {values.meetingUrl ? (
                <div className={css.videoLinkGenerated}>
                  <a
                    href={values.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.videoLinkChip}
                    title={values.meetingUrl}
                  >
                    <Icon name="video" className={css.videoLinkIcon} />
                    <span className={css.videoLinkChipText}>
                      {values.meetingUrl.replace(/^https?:\/\//, '').slice(0, 20)}…
                    </span>
                  </a>
                  <div className={css.videoLinkGeneratedActions}>
                    {meetingLinkCopied ? (
                      <span className={css.videoLinkCopied}>
                        {intl.formatMessage({
                          id: 'CreateEditEventForm.meetingCopied',
                        })}
                      </span>
                    ) : (
                      <InlineTextButton
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(values.meetingUrl);
                            setMeetingLinkCopied(true);
                            setTimeout(() => setMeetingLinkCopied(false), 2000);
                          } catch (e) {
                            // clipboard unavailable — noop
                          }
                        }}
                      >
                        {intl.formatMessage({
                          id: 'CreateEditEventForm.copyMeeting',
                        })}
                      </InlineTextButton>
                    )}
                    <InlineTextButton
                      type="button"
                      onClick={() => {
                        form.change('meetingUrl', '');
                        form.change('meetingHostUrl', '');
                        form.change('meetingHostUserId', '');
                        setMeetingLinkCopied(false);
                      }}
                    >
                      {intl.formatMessage({
                        id: 'CreateEditEventForm.removeMeeting',
                      })}
                    </InlineTextButton>
                  </div>
                </div>
              ) : (
                <SecondaryButton
                  type="button"
                  className={css.wherebyButton}
                  inProgress={generatingMeeting}
                  disabled={generatingMeeting || !onGenerateMeeting}
                  onClick={handleGenerateMeeting}
                >
                  {autoGenerateLabel}
                </SecondaryButton>
              )}
              {generateMeetingError && (
                <p className={css.videoLinkError}>
                  {intl.formatMessage({
                    id: 'CreateEditEventForm.generateMeetingFailed',
                  })}
                </p>
              )}
            </div>

            {/* Location */}
            <FieldTextInput
              id="location"
              name="location"
              type="text"
              className={css.field}
              label={locationLabel}
              placeholder={locationPlaceholder}
            />

            {/* Participants: an org's team from the page, everyone else by
                searching the organizer's own audience or typing an address. */}
            {(participantOptions.length > 0 || onSearchParticipants) && (
              <ParticipantsField
                options={participantOptions}
                onSearch={onSearchParticipants}
                intl={intl}
              />
            )}

            {/* The invitation itself, only once there is someone to invite. */}
            {hasParticipants && (
              <div className={css.inviteBlock}>
                <FieldTextInput
                  id="inviteNote"
                  name="inviteNote"
                  type="text"
                  className={css.field}
                  label={inviteNoteLabel}
                  placeholder={inviteNotePlaceholder}
                  maxLength={INVITE_NOTE_MAX}
                  validate={validators.maxLength(inviteNoteTooLong, INVITE_NOTE_MAX)}
                />
                <div className={css.checkboxField}>
                  <FieldCheckbox id="sendInvites" name="sendInvites" label={sendInvitesLabel} />
                  <p className={css.inviteHint}>{sendInvitesHint}</p>
                </div>
              </div>
            )}

            {/* Error message */}
            {formError && (
              <p className={css.error}>
                <FormattedMessage id="CreateEditEventForm.submitFailed" />
              </p>
            )}

            {/* Buttons */}
            <div className={css.buttonRow}>
              <button
                type="button"
                className={css.cancelButton}
                onClick={onCancel}
                disabled={submitInProgress}
              >
                {cancelButtonText}
              </button>
              <button type="submit" className={css.submitButton} disabled={submitDisabled}>
                {submitButtonText}
              </button>
            </div>
          </Form>
        );
      }}
    />
  );
};

CreateEditEventFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  isEditMode: false,
  submitInProgress: false,
  formError: null,
  initialValues: {},
};

CreateEditEventFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  isEditMode: bool,
  submitInProgress: bool,
  formError: object,
  initialValues: object,
};

// Helper function to prepare event data for submission
export const prepareEventDataForSubmit = (values, existingEventId = null) => {
  const {
    title,
    allDay,
    startDate,
    startTime,
    endDate,
    endTime,
    timeZone,
    description,
    meetingUrl,
    meetingHostUrl,
    meetingHostUserId,
    location,
  } = values;

  // Combine date and time
  // When all day: startDate at 00:00 -> endDate at 23:59 (already supports
  // multi-day).
  // When not all day: startDate + startTime -> endDate + endTime. endDate
  // defaults to startDate (set by handleStartDateChange) so single-day
  // events keep working; for multi-day rehearsals/touring schedules the
  // user picks a later endDate.
  const startDateTime = allDay
    ? new Date(new Date(startDate.date).setHours(0, 0, 0, 0))
    : combineDateAndTime(startDate.date, startTime, timeZone);

  const endDateBase = endDate?.date || startDate.date;
  const endDateTime = allDay
    ? new Date(new Date(endDateBase).setHours(23, 59, 59, 999))
    : combineDateAndTime(endDateBase, endTime, timeZone);

  const now = new Date().toISOString();

  return {
    id: existingEventId || `evt_${uuidv4()}`,
    type: 'user-event',
    title: title.trim(),
    description: description?.trim() || '',
    start: startDateTime.toISOString(),
    end: endDateTime.toISOString(),
    allDay: !!allDay,
    timeZone,
    // hostUrl grants Whereby host controls; hostUserId marks who created it so
    // only that user is handed the host link on join.
    meeting: meetingUrl
      ? {
          url: meetingUrl.trim(),
          hostUrl: meetingHostUrl || null,
          hostUserId: meetingHostUserId || null,
        }
      : null,
    location: location ? { address: location.trim() } : null,
    // Selected participants. Invitations are sent by the server after the
    // event is saved (see server/api-util/calendarInvites.js); an event saved
    // with sendInvites off is stored without mailing anyone.
    attendees: Array.isArray(values.participants) ? values.participants : [],
    inviteNote:
      String(values.inviteNote || '')
        .trim()
        .slice(0, INVITE_NOTE_MAX) || null,
    sendInvites: values.sendInvites !== false,
    createdAt: existingEventId ? undefined : now, // Only set on create
    updatedAt: now,
  };
};

// Helper function to prepare initial values from existing event
export const prepareInitialValuesFromEvent = event => {
  if (!event) {
    // Default values for new event
    const defaultTimeZone =
      typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

    return {
      title: '',
      allDay: false,
      startDate: null,
      startTime: '09:00',
      endDate: null,
      endTime: '10:00',
      timeZone: defaultTimeZone,
      description: '',
      meetingUrl: '',
      meetingHostUrl: '',
      meetingHostUserId: '',
      location: '',
      participants: [],
      inviteNote: '',
      sendInvites: true,
    };
  }

  const startDate = new Date(event.start);
  const endDate = new Date(event.end);

  return {
    title: event.title || '',
    allDay: event.allDay || false,
    startDate: { date: startDate },
    startTime: extractTimeFromDate(startDate),
    endDate: { date: endDate },
    endTime: extractTimeFromDate(endDate),
    timeZone: event.timeZone || getDefaultTimeZoneOnBrowser() || 'Etc/UTC',
    description: event.description || '',
    meetingUrl: event.meeting?.url || '',
    meetingHostUrl: event.meeting?.hostUrl || '',
    meetingHostUserId: event.meeting?.hostUserId || '',
    location: event.location?.address || '',
    participants: Array.isArray(event.attendees) ? event.attendees : [],
    inviteNote: event.inviteNote || '',
    // Always on when the modal opens: only people not yet invited are mailed.
    sendInvites: true,
  };
};

const CreateEditEventForm = compose(injectIntl)(CreateEditEventFormComponent);
CreateEditEventForm.displayName = 'CreateEditEventForm';

export default CreateEditEventForm;
